import { useCallback, useMemo, useState } from 'react';
import { BudgetScenarioWorktypeOption } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import {
    DraftIssue,
    DraftUpdater,
    ScenarioContactsDraftEntity,
    contactsTrainingCost,
    entityRowHasValues,
    patchDraftMonthValue,
    patchTrainingRowField,
    removeDraftRow,
} from './scenarioDraft';
import {
    formatCurrency,
    formatDecimal,
    integerFormatter,
    parseBudgetCell,
    SCENARIO_ENTITY_COL_MIN_WIDTH,
} from './helpers';
import ScenarioRowGrid, { ScenarioRowColumn, ScenarioRowOption } from './ScenarioRowGrid';

interface ScenarioContactsGridProps {
    months: string[];
    /** One row per worktype the scope multiselect picked — its training block and its monthly deltas. Read for
     *  RENDERING only: an edit is derived from the `previous` an updater is handed, never from this prop. */
    entities: ScenarioContactsDraftEntity[];
    onChange: (update: DraftUpdater<ScenarioContactsDraftEntity[]>) => void;
    issues: DraftIssue[];
    availableWorktypes: BudgetScenarioWorktypeOption[];
    isLoading: boolean;
    canEdit: boolean;
    /** National public-holiday dates ('YYYY-MM-DD') the Trainingstage prefill subtracts besides weekends
     *  (CALL-801) — the same set the backend's training allocation uses. Undefined while the page is still
     *  loading them, which suppresses the prefill rather than counting holidays as working days. */
    holidays?: ReadonlySet<string>;
}

// Input grid for a Kontakte scenario: one row per worktype with its training block (Agents, Trainingstage,
// €/Stunde, Trainerressource) and its monthly contact deltas, which may be negative. `Trainingskosten` is
// derived, not entered. Inputs only — the calculated impact (contacts cost + the one-time training cost) is
// the section's other view.
//
// The training block (CALL-783 Stage 4, board frame screen_003) is what moved contacts off ScenarioValueGrid:
// with a row of scalars in front of a monthly series it is structurally the same thing as the Verschiebung
// and GA/CB/VVL grids, so it shares their mechanics instead of growing a third variant. Cells stay WHOLE
// contacts — the month format and the rounding-on-parse are this file's only cell-level specialisation.
//
// Everything mechanical lives in ScenarioRowGrid; this file owns the column list, the per-row worktype
// choices and the edit handlers. Like Driver there is no add-row button: the rows ARE the form's worktype
// multiselect (one row per worktype), and the trash writes back into that selection. Edits report a new
// draft upward; the page's single Save commits them.
const ScenarioContactsGrid = ({
    months,
    entities,
    onChange,
    issues,
    availableWorktypes,
    isLoading,
    canEdit,
    holidays,
}: ScenarioContactsGridProps) => {
    const { t } = useTranslation('budget');
    // Removing a row that holds values (deltas OR training) discards them on the next save — confirm first.
    // A still-blank row can go without friction.
    const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

    // Worktypes already taken by other rows — excluded from each row's picker so a worktype is used at most
    // once (the deltas and the training block are both keyed by scenario + worktype server-side).
    const usedWorktypeIds = useMemo(() => entities.map((entity) => entity.entityId), [entities]);

    const worktypeOptionsFor = useCallback(
        (row: ScenarioContactsDraftEntity): ScenarioRowOption[] => {
            const options = availableWorktypes
                .filter((option) => option.worktypeId === row.entityId || !usedWorktypeIds.includes(option.worktypeId))
                .map((option) => ({ id: option.worktypeId, label: option.worktypeName }));
            // A persisted worktype no longer offered by the bucket (e.g. after the scenario is rebased or a
            // billing config is removed) still renders with its saved label rather than a blank,
            // out-of-range value.
            if (!options.some((option) => option.id === row.entityId)) {
                options.push({ id: row.entityId, label: row.entityLabel ?? `#${row.entityId}` });
            }
            return options;
        },
        [availableWorktypes, usedWorktypeIds]
    );

    // Board order (screen_003): the worktype, its training block with the derived total, then the monthly
    // contact deltas. Only the worktype column is wide (SCENARIO_ENTITY_COL_MIN_WIDTH, the width its
    // impact-grid counterpart pins at); the training block holds 2-5 digits, as on the board — and since
    // CALL-784 it is sized for those digits rather than for the captions, which truncate with a tooltip.
    const columns = useMemo<ScenarioRowColumn<ScenarioContactsDraftEntity>[]>(
        () => [
            {
                kind: 'select',
                key: 'entityId',
                headerName: t('field.worktype'),
                width: SCENARIO_ENTITY_COL_MIN_WIDTH,
                pinned: 'left',
                options: worktypeOptionsFor,
            },
            // The training window (CALL-801), exactly like the Verschiebung grid's pair — 78 is the
            // smallest width that shows a full German date, see ScenarioShiftGrid for the arithmetic.
            // Unlike there, Start may stay empty: the cost is then spread from the scenario start (and a
            // new row is prefilled with that date, so the fallback is visible rather than implicit).
            { kind: 'date', key: 'trainingStart', headerName: t('field.trainingStart'), width: 78 },
            { kind: 'date', key: 'trainingEnd', headerName: t('field.trainingEnd'), width: 78 },
            // 70 is the shared wrapper's `minWidth` floor — anything smaller renders at 70 regardless.
            { kind: 'number', key: 'agents', headerName: t('field.agents'), width: 70, format: formatDecimal },
            {
                kind: 'number',
                key: 'trainingDays',
                headerName: t('field.trainingDays'),
                width: 92,
                format: formatDecimal,
            },
            {
                kind: 'number',
                key: 'costPerHour',
                headerName: t('field.costPerHour'),
                width: 70,
                format: formatCurrency,
            },
            {
                // The board calls the fixed part "Trainerressource" here, as on the GA/CB/VVL screen — the
                // persisted field is `trainerCost`, shared with the shift row.
                kind: 'number',
                key: 'trainerCost',
                headerName: t('field.trainerResource'),
                width: 90,
                format: formatCurrency,
            },
            {
                // Six-figure euro total (€60.480 on the board) — the one numeric column that needs the room.
                kind: 'computed',
                field: 'trainingTotal',
                headerName: t('field.trainingTotal'),
                width: 92,
                compute: contactsTrainingCost,
                format: formatCurrency,
            },
        ],
        [t, worktypeOptionsFor]
    );

    // Every handler below patches the draft it is HANDED, never the `entities` prop: one gesture (Ctrl+D down a
    // column, a paste across the training block) fires several cellValueChanged events in a single macrotask,
    // with no render between them, so a patch built from the prop would be built from the pre-burst state and
    // only the last would survive (see DraftUpdater in scenarioDraft).
    const onFieldChange = useCallback(
        (clientKey: string, key: keyof ScenarioContactsDraftEntity & string, value: number | string) => {
            // The worktype dropdown has no blank option, so it can only ever report a real id — but
            // `entityId` is typed `number` (the shape is shared with Minute-price), so a cleared value is
            // dropped rather than written as ''.
            if (key === 'entityId' && value === '') return;
            // patchTrainingRowField (CALL-801): Start/Ende prefill Trainingstage exactly as on the
            // Verschiebung grid; for every other key it degrades to the plain single-field patch.
            onChange((previous) => patchTrainingRowField(previous, clientKey, key, value, holidays));
        },
        [onChange, holidays]
    );

    const onRemoveRow = useCallback(
        (row: ScenarioContactsDraftEntity) => {
            if (!entityRowHasValues(row)) {
                onChange((previous) => removeDraftRow(previous, row.clientKey));
                return;
            }
            setDeleteConfirmKey(row.clientKey);
        },
        [onChange]
    );

    const onConfirmDelete = useCallback(() => {
        if (deleteConfirmKey === null) return;
        onChange((previous) => removeDraftRow(previous, deleteConfirmKey));
        setDeleteConfirmKey(null);
    }, [onChange, deleteConfirmKey]);

    const rowLabel = useCallback(
        (row: ScenarioContactsDraftEntity) =>
            worktypeOptionsFor(row).find((option) => option.id === row.entityId)?.label ?? '',
        [worktypeOptionsFor]
    );

    const formatMonthValue = useCallback(
        (value: unknown) => (value == null || value === '' ? '' : integerFormatter.format(Number(value))),
        []
    );

    // Contacts are whole calls — round what the user typed instead of storing a fractional contact.
    const parseMonthValue = useCallback((rawValue: unknown) => {
        const parsed = parseBudgetCell(rawValue);
        return parsed !== null ? Math.round(parsed) : null;
    }, []);

    return (
        <ScenarioRowGrid<ScenarioContactsDraftEntity>
            months={months}
            rows={entities}
            columns={columns}
            isLoading={isLoading}
            canEdit={canEdit}
            issues={issues}
            formatMonthValue={formatMonthValue}
            parseMonthValue={parseMonthValue}
            rowLabel={rowLabel}
            onFieldChange={onFieldChange}
            onMonthValueChange={(clientKey, month, value) =>
                onChange((previous) => patchDraftMonthValue(previous, clientKey, month, value))
            }
            onRemoveRow={onRemoveRow}
            deleteConfirmOpen={deleteConfirmKey !== null}
            onCancelDelete={() => setDeleteConfirmKey(null)}
            onConfirmDelete={onConfirmDelete}
            labels={{
                noRows: t('scenarioDetail.noWorktypes'),
                deleteRow: t('scenarioDetail.deleteContactRow'),
            }}
        />
    );
};

export default ScenarioContactsGrid;
